import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Home, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Confetti from 'react-confetti';
import { calculateTotal } from '../lib/orderUtils';

const ConfirmationHeader = () => (
  <>
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
      className="w-32 h-32 mx-auto mb-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-[0_0_50px_hsl(142_76%_36%/0.4)]"
    >
      <CheckCircle2 className="w-20 h-20 text-white" strokeWidth={2} />
    </motion.div>

    <motion.h1
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="text-4xl md:text-5xl font-bold text-foreground mb-4"
    >
      Bedankt!
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="text-xl text-muted-foreground mb-8"
    >
      Je bestelling is succesvol geplaatst
    </motion.p>
  </>
);

const OrderIdSection = ({ orderId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5 }}
    className="bg-muted/30 rounded-2xl p-6 mb-8"
  >
    <p className="text-sm text-muted-foreground mb-2">Bestelnummer</p>
    <p className="text-3xl font-bold text-primary font-mono">{orderId || 'PKA-000000'}</p>
  </motion.div>
);

const OrderSummaryCard = ({ order, total }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.6 }}
    className="text-left bg-muted/20 rounded-2xl p-6 mb-8"
  >
    <h3 className="text-lg font-semibold text-foreground mb-4">Je bestelling</h3>
    <div className="space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-muted-foreground">{order.product?.name}</span>
        <span className="text-foreground">{'\u20AC'}{order.product?.price.toFixed(2)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">{order.flavor?.name}</span>
        <span className="text-foreground">-</span>
      </div>

      {order.burger && (
        <>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Burger (met broodje)</span>
            <span className="text-foreground">+{'\u20AC'}1.50</span>
          </div>
          {order.burgerSauce && order.burgerSauce.price > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">{order.burgerSauce.name}</span>
              <span className="text-foreground">+{'\u20AC'}{order.burgerSauce.price.toFixed(2)}</span>
            </div>
          )}
        </>
      )}

      <div className="flex justify-between font-medium">
        <span className="text-foreground">Signature Ribbled Chips</span>
        <span className="text-primary">GRATIS</span>
      </div>

      {order.extraSides && order.extraSides.length > 0 && (
        order.extraSides.map((extra) => (
          <div key={extra.id} className="flex justify-between">
            <span className="text-muted-foreground">
              {extra.name} {extra.quantity > 1 && `x${extra.quantity}`}
            </span>
            <span className="text-foreground">{'\u20AC'}{(extra.price * extra.quantity).toFixed(2)}</span>
          </div>
        ))
      )}

      <div className="border-t border-border pt-2 mt-2 flex justify-between font-bold">
        <span className="text-foreground">Totaal</span>
        <span className="text-primary text-lg">{'\u20AC'}{total.toFixed(2)}</span>
      </div>
    </div>
  </motion.div>
);

export const OrderConfirmation = ({ order, resetOrder }) => {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = React.useState(true);
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  const handleResize = useCallback(() => {
    setWindowSize({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleNewOrder = () => {
    resetOrder();
    navigate('/');
  };

  const total = calculateTotal(order);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative">
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
        />
      )}

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <Card className="p-12 bg-card border-2 border-border text-center">
          <ConfirmationHeader />
          <OrderIdSection orderId={order.orderId} />
          <OrderSummaryCard order={order} total={total} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex items-center justify-center gap-3 mb-8 text-muted-foreground"
          >
            <Clock className="w-5 h-5" />
            <span>Bereidingstijd: ~15 minuten</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <Button
              onClick={handleNewOrder}
              size="lg"
              data-testid="new-order-btn"
              className="btn-kiosk bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-6 text-lg font-semibold shadow-lg w-full"
            >
              <Home className="mr-2 w-5 h-5" />
              Nieuwe bestelling
            </Button>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  );
};

export default OrderConfirmation;
